/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { QnaMakerAzureSearchPage } from 'src/qnaMaker/appShell/qnaMakerAzureSearchPage/QnaMakerAzureSearchPage';
import { QnaMakerProjectPage } from 'src/qnaMaker/appShell/QnaMakerProjectPage';
import { QnaMakerProjectsPage } from 'src/qnaMaker/appShell/QnaMakerProjectsPage/QnaMakerProjectsPage';
import { QnaMakerBreadcrumb } from 'src/qnaMaker/core/components/QnaMakerBreadcrumb';
import { useQnaMakerStartupConfigs } from 'src/qnaMaker/core/hooks/useQnaMakerStartupConfigs';
import { QnaMakerSidebar } from 'src/qnaMaker/modules/QnaMakerSidebar';

const { styleDiv, styleComponent } = getStylist('QnaMakerRoutes');
const BreadcrumbWrapper = styleDiv('QnaMakerRoutes', { paddingLeft: 24 });
const QnaRoutesContainer = styleComponent<IStackProps>(Stack)('QnaRoutesContainer', { overflow: 'hidden' });
const QnaContentContainer = styleComponent<IStackProps>(Stack)('QnaContentContainer', { overflow: 'hidden' });

const renderProjectPage = (props: RouteComponentProps<{ projectId: string }>) => {
    const { params } = props.match;
    const encodedCsAccountPath = new URLSearchParams(props.location.search).get('csAccountPath');
    const csAccountPath = encodedCsAccountPath !== null ? decodeURIComponent(encodedCsAccountPath) : undefined;

    return <QnaMakerProjectPage projectId={params.projectId} csAccountPath={csAccountPath} />;
};

export const QnaMakerRoutes = observer(() => {
    useQnaMakerStartupConfigs();
    // eslint-disable-next-line global-require
    require('@ckeditor/ckeditor5-build-classic/build/ckstyles.css');

    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;

    return (
        <QnaRoutesContainer horizontal grow>
            <QnaMakerSidebar />

            <QnaContentContainer grow>
                <BreadcrumbWrapper>
                    <QnaMakerBreadcrumb />
                </BreadcrumbWrapper>

                <Switch>
                    {!isSignedIn && [<Route component={QnaMakerProjectsPage} path={routeTable.qnaMaker.projectsPage()} />]}
                    <PrivateRoute exact path={routeTable.qnaMaker.projectsPage()} component={QnaMakerProjectsPage} />
                    <PrivateRoute exact path={routeTable.qnaMaker.azureSearchPage()} component={QnaMakerAzureSearchPage} />
                    <PrivateRoute path={routeTable.qnaMaker.projectPage()} render={renderProjectPage} />
                    <Redirect exact from={routeTable.qnaMaker.root()} to={routeTable.qnaMaker.projectsPage()} />
                    <NotFoundRedirect />
                </Switch>
            </QnaContentContainer>
        </QnaRoutesContainer>
    );
});
